import { Suspense, lazy, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { AppShell } from "../components/AppShell";
import { Card, ErrorBox, Skeleton } from "../components/ui";
import { SCREENS, screenLabel } from "../erp/registry";
import { useAiHealth, useLakeStatus } from "../lib/api";
import { ROLE_LABEL_KEYS } from "../lib/auth";
import { modulesFor } from "../lib/nav";
import ApprovalIntegrity from "./admin/ApprovalIntegrity";
import NotificationSettings from "./admin/NotificationSettings";
import EmployeeList from "./EmployeeList";

// the add-ons card probes the OneFleet services (socket.io client included): loaded only when the tab opens
const AddOns = lazy(() => import("./admin/AddOns"));

type Tab = "consoles" | "config" | "notifications" | "approvals" | "roles" | "ai" | "addons";
const TABS: Tab[] = ["consoles", "config", "notifications", "approvals", "roles", "ai", "addons"];

/** Development ports of the compose stack (docker-compose.yml); production binds the consoles behind the reverse proxy. */
const CONSOLES: { key: string; port?: number; path: string }[] = [
  { key: "erpnext", port: 51020, path: "/app" },
  { key: "keycloak", port: 51081, path: "/admin/onesphere/console/" },
  { key: "ai", path: "/ai/docs" },
  { key: "bifrost", port: 51091, path: "/" },
  { key: "superset", port: 51088, path: "/" },
  { key: "trino", port: 51070, path: "/ui/" },
  { key: "seaweed", port: 51888, path: "/" },
];
/** Configuration by module (requirement 9.2: "from POS to Finance to every other module"): every setup and master
 * record an administrator needs, grouped the way the business lines are. The cards **name** them and link nowhere:
 * the generated ERP screens were removed from the application on 2026-09-28 (owner's rule — OneSphere's users work
 * OneSphere's modules, and an administrator who needs the desk opens ERPNext's own URL, which the Consoles tab
 * prints).
 * Read access for the core Frappe/ERPNext doctypes here comes from ADMIN_SETUP_READ_DOCTYPES in setup/install.py;
 * the OneSphere doctypes (KPI Definition, Incentive Plan, OneSphere Action, Plant) carry OneSphere Admin in their
 * own DocType permissions. */
type ConfigGroupKey = "organisation" | "pos" | "finance" | "sales" | "buying" | "stock" | "hr" | "projects" | "onesphere";
const CONFIG_GROUPS: { key: ConfigGroupKey; doctypes: string[] }[] = [
  { key: "organisation", doctypes: ["Company", "Plant", "Department", "Cost Center", "Currency", "Fiscal Year"] },
  { key: "pos", doctypes: ["POS Profile", "Mode of Payment", "POS Closing Entry"] },
  { key: "finance", doctypes: ["Account", "Payment Terms Template", "Purchase Taxes and Charges Template", "Sales Taxes and Charges Template", "Currency Exchange", "Budget"] },
  { key: "sales", doctypes: ["Customer Group", "Territory", "Sales Person", "Price List", "Pricing Rule"] },
  { key: "buying", doctypes: ["Supplier Group"] },
  { key: "stock", doctypes: ["Item Group", "Brand", "UOM", "Warehouse Type"] },
  { key: "hr", doctypes: ["Employment Type", "Holiday List", "Leave Type", "Shift Type", "Salary Component", "Salary Structure"] },
  { key: "projects", doctypes: ["Activity Type"] },
  { key: "onesphere", doctypes: ["KPI Definition", "Incentive Plan", "OneSphere Action"] },
];
/** Realm role → ERPNext role (mirrors ROLE_MAP in onesphere/auth.py). */
const ERP_ROLE: Record<string, string> = {
  os_executive: "OneSphere Executive", os_plant_gm: "OneSphere Plant GM", os_finance: "OneSphere Finance", os_hr: "OneSphere HR", os_project_manager: "OneSphere Project Manager",
  os_procurement: "OneSphere Procurement", os_maintenance: "OneSphere Maintenance", os_operator: "OneSphere Operator", os_cashier: "OneSphere Cashier", os_store_manager: "OneSphere Store Manager",
  os_sales: "OneSphere Sales", os_admin: "OneSphere Admin",
};

function consoleUrl(c: { port?: number; path: string }): string {
  if (!c.port) return c.path;
  const host = typeof window !== "undefined" ? window.location.hostname : "localhost";
  return `http://${host}:${c.port}${c.path}`;
}

/** Administration (requirement 9.2, WP-03.6): platform consoles, configuration records, roles and the modules they open,
 * AI gateway and data-lake health. Administrators only; everything else is read through the ERP screens. */
export default function Admin() {
  const { t, i18n } = useTranslation();
  const { search } = useLocation();
  const { identity } = useAuth();
  const [tab, setTab] = useState<Tab>("consoles");
  useEffect(() => {
    const tb = new URLSearchParams(search).get("tab");
    if (tb && (TABS as string[]).includes(tb)) setTab(tb as Tab);
  }, [search]);
  const isAdmin = (identity?.roles ?? []).includes("os_admin");
  const health = useAiHealth(tab === "ai");
  const lake = useLakeStatus(tab === "ai" && isAdmin);
  return (
    <AppShell subtitle={t("nav.admin.title")}>
      <h1 className="font-heading text-2xl font-semibold">{t("admin.title")}</h1>
      <p className="text-sm text-brand-grey">{t("admin.subtitle")}</p>
      {!isAdmin && <Card className="mt-4 border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-admin-locked>{t("nav.adminOnly")}</Card>}
      <div className="mt-4 flex flex-wrap gap-1 border-b border-brand-grey-light" role="tablist" aria-label="admin-sections">
        {TABS.map((k) => (
          <Link key={k} to={`/admin?tab=${k}`} role="tab" aria-selected={tab === k}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === k ? "border-brand-primary text-brand-primary" : "border-transparent text-brand-grey hover:text-brand-charcoal"}`}>
            {t(`admin.tabs.${k}`)}
          </Link>
        ))}
      </div>

      {tab === "notifications" && <NotificationSettings />}

      {tab === "approvals" && <ApprovalIntegrity />}

      {tab === "addons" && <Suspense fallback={<Skeleton className="mt-4 h-40" />}><AddOns active /></Suspense>}

      {tab === "consoles" && (
        <section className="mt-4" data-admin-consoles>
          <p className="text-xs text-brand-grey">{t("admin.consoles.hint")}</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {CONSOLES.map((c) => (
              <Card key={c.key} className="flex items-center justify-between px-4 py-3">
                <div>
                  <div className="text-sm font-semibold">{t(`admin.consoles.${c.key}`)}</div>
                  <div className="text-[11px] text-brand-grey">{consoleUrl(c)}</div>
                </div>
                <a href={consoleUrl(c)} target="_blank" rel="noreferrer" className="rounded border border-brand-primary px-2 py-1 text-xs font-semibold text-brand-primary hover:bg-blue-50">{t("admin.consoles.open")} ↗</a>
              </Card>
            ))}
          </div>
        </section>
      )}

      {tab === "config" && (
        <section className="mt-4 space-y-6" data-admin-config>
          <p className="text-xs text-brand-grey">{t("admin.config.hint")}</p>
          {CONFIG_GROUPS.map((g) => (
            <div key={g.key} data-admin-config-group={g.key}>
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t(`admin.config.groups.${g.key}`)}</h2>
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {g.doctypes.map((doctype) => {
                  const screen = SCREENS.find((s) => s.doctype === doctype);
                  return (
                    <Card key={doctype} className="px-4 py-3">
                      <div className="text-sm font-semibold">{screenLabel(screen, i18n.language, doctype)}</div>
                      <div className="text-[11px] text-brand-grey">{doctype}</div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
          <div>
            <h2 className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("admin.config.users")}</h2>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <a href={consoleUrl({ port: 51081, path: "/admin/onesphere/console/#/onesphere/users" })} target="_blank" rel="noreferrer">
                <Card className="flex items-center justify-between px-4 py-3 transition hover:-translate-y-0.5 hover:shadow-md">
                  <div>
                    <div className="text-sm font-semibold">Keycloak</div>
                    <div className="text-[11px] text-brand-grey">{t("admin.config.usersKeycloak")}</div>
                  </div>
                  <span className="text-brand-grey">↗</span>
                </Card>
              </a>
              <a href={consoleUrl({ port: 51020, path: "/app/user" })} target="_blank" rel="noreferrer">
                <Card className="flex items-center justify-between px-4 py-3 transition hover:-translate-y-0.5 hover:shadow-md">
                  <div>
                    <div className="text-sm font-semibold">ERPNext</div>
                    <div className="text-[11px] text-brand-grey">{t("admin.config.usersErpnext")}</div>
                  </div>
                  <span className="text-brand-grey">↗</span>
                </Card>
              </a>
            </div>
          </div>
          <div data-admin-employee-accounts>
            <h2 className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("admin.config.employeeAccounts")}</h2>
            <p className="mt-1 text-xs text-brand-grey">{t("admin.config.employeeAccountsHint")}</p>
            <EmployeeList />
          </div>
        </section>
      )}

      {tab === "roles" && (
        <section className="mt-4" data-admin-roles>
          <p className="text-xs text-brand-grey">{t("admin.roles.hint")}</p>
          <Card className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr><th className="px-4 py-2">{t("admin.roles.role")}</th><th className="px-4 py-2">{t("admin.roles.realm")}</th><th className="px-4 py-2">ERPNext</th><th className="px-4 py-2">{t("admin.roles.modules")}</th></tr>
              </thead>
              <tbody>
                {Object.keys(ERP_ROLE).map((r) => (
                  <tr key={r} className="border-t border-brand-grey-light align-top">
                    <td className="px-4 py-2 font-semibold">{t(ROLE_LABEL_KEYS[r] ?? r)}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r}</td>
                    <td className="px-4 py-2 text-xs">{ERP_ROLE[r]}</td>
                    <td className="px-4 py-2 text-xs text-brand-grey">{modulesFor([r]).filter((m) => m.key !== "my" && m.key !== "erp").map((m) => t(m.label)).join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </section>
      )}

      {tab === "ai" && (
        <section className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2" data-admin-ai>
          <Card className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">{t("admin.ai.gateway")}</div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${health.isSuccess ? "bg-green-100 text-green-800" : health.isError ? "bg-red-100 text-red-800" : "bg-brand-grey-light text-brand-grey"}`}>
                {health.isSuccess ? t("admin.ai.ok") : health.isError ? t("admin.ai.down") : t("admin.ai.checking")}
              </span>
            </div>
            <p className="mt-1 text-xs text-brand-grey">{t("admin.ai.hint")}</p>
            {health.isLoading && <Skeleton className="mt-3 h-16" />}
            {health.data && (
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <dt className="text-brand-grey">{t("admin.ai.version")}</dt><dd>{health.data.version}</dd>
                <dt className="text-brand-grey">{t("admin.ai.model")}</dt><dd className="font-mono">{health.data.primary_model}</dd>
                <dt className="text-brand-grey">Bifrost</dt><dd className="truncate font-mono" title={health.data.gateway}>{health.data.gateway}</dd>
                <dt className="text-brand-grey">Cloud</dt><dd>{health.data.cloud_enabled ? "on" : "off"}</dd>
              </dl>
            )}
            {health.isError && <div className="mt-3"><ErrorBox error={health.error} onRetry={() => void health.refetch()} /></div>}
          </Card>
          <Card className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">{t("admin.ai.lake")}</div>
              <button type="button" onClick={() => void lake.refetch()} className="rounded border border-brand-grey-light px-2 py-0.5 text-xs hover:border-brand-primary">{t("admin.ai.refresh")}</button>
            </div>
            {lake.isLoading && <Skeleton className="mt-3 h-16" />}
            {lake.isError && <div className="mt-3"><ErrorBox error={lake.error} onRetry={() => void lake.refetch()} /></div>}
            {lake.data && <pre className="mt-3 max-h-72 overflow-auto rounded bg-[#F6F8FA] p-3 text-[11px] leading-snug">{JSON.stringify(lake.data, null, 1)}</pre>}
          </Card>
          <Card className="px-4 py-3 lg:col-span-2">
            <div className="text-sm font-semibold">{t("admin.demo.title")}</div>
            <p className="mt-1 text-xs text-brand-grey">{t("admin.demo.hint")}</p>
            <ul className="mt-2 list-disc pl-5 text-xs text-brand-charcoal">
              <li><span className="font-mono">docs/OneSphere-Demo-Runbook.md</span> — {t("admin.demo.runbook")}</li>
              <li><span className="font-mono">docs/OneSphere-Demo-Scripts.md</span> — {t("admin.demo.scripts")}</li>
              <li><span className="font-mono">scripts/verify-erp.sh · scripts/verify-ai.sh</span> — {t("admin.demo.verify")}</li>
            </ul>
          </Card>
        </section>
      )}
    </AppShell>
  );
}
